import Vue from 'vue'

import dateHelper from '../functions/dateHelper.js'
import timeFromGap from '../functions/timeFromGap.js'
import tracking from '../functions/tracking.js'

import { BREAKPOINT_SIDEBAR } from '../settings.js'

export function setLoading (state, loading) {
  state.loading = loading
}

export function setLabels (state, labels) {
  state.labels = labels
}

export function setConfig (state, config) {
  state.config = config
}

export function reset (state) {
  state.showTicketSelection = false

  state.selectedTicketsAmount = 0
  state.selectedReservationsAmount = 0

  state.voucherSelection = {}
}

export function setSelectedDate (state, date) {
  state.ticketSelection.selectedDate = date
}

export function setMaxDate (state, maxDate) {
  state.maxDate = maxDate
}

export function setShowTickets (state, show) {
  state.showTicketSelection = show
}

export function showContainerSidebar (state, show) {
  state.showContainerSidebar = show

  if (show && !BREAKPOINT_SIDEBAR.matches) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
}

// Dates

export function updateArrivalTimeDates (state, dates) {
  const today = dateHelper.getDate()
  const prevDate = dateHelper.dateFromString(dates.previous)
  prevDate.setHours(0, 0, 0, 0)

  state.arrivalTable.previous.selectable = (prevDate.getTime() >= today.getTime())

  state.arrivalTable.previous.date = dates.previous
  state.arrivalTable.active.date = dates.active
  state.arrivalTable.next.date = dates.next

  state.ticketSelection.selectedDate = dates.active
}

// Gaps

export function updateGaps (state, gapResponse) {
  const minimalGap = 11

  for (const gapDate in gapResponse) {
    const gapData = {
      lastRequest: new Date().getTime(),
      gaps: gapResponse[gapDate]
    }

    gapData.earliestGap = 100
    gapData.latestGap = 0
    gapData.openingGap = 100

    const gapCount = gapData.gaps.length

    for (let key = 1; key < gapCount; key++) {
      if (key in gapData.gaps) {
        const gap = gapData.gaps[key]

        if (key === gapCount - 1) {
          gap.is_open = false
        }

        // We ignore the last gap before we close. Technically it's open, but you can't book it
        if (key + 1 in gapData.gaps) {
          const nextGap = gapData.gaps[key + 1]
          if (nextGap.is_open === false) {
            gap.is_open = false
          }
        }

        if (gap.is_open === true) {
          if (gap.gap > minimalGap && gap.gap < gapData.openingGap) {
            gapData.openingGap = gap.gap
          }
          if (gap.gap > minimalGap && gap.gap < gapData.earliestGap) {
            gapData.earliestGap = gap.gap
          }
          if (gap.gap > gapData.latestGap) {
            gapData.latestGap = gap.gap
          }
        }
      }
    }

    gapData.openingHours = timeFromGap(gapData.openingGap, true, false) + ' – ' + timeFromGap(gapData.latestGap, false, true, true)
    Vue.set(state.gaps, gapDate, gapData)
  }

  // cleanup
  const currentGapDate = new Date(state.arrivalTable.active.date)
  const minDate = dateHelper.dayBefore(currentGapDate, 3)
  const maxDate = dateHelper.dayAfter(currentGapDate, 3)
  const gaps = state.gaps

  for (const gapsKey in gaps) {
    const gapDate = new Date(gapsKey)
    if (gapDate < minDate || gapDate > maxDate) {
      delete gaps[gapsKey]
    }

    // Check if there is an open gap at the next day and adjust the closing time accordingly
    const nextDay = dateHelper.formatDate(dateHelper.dayAfter(gapDate))
    if (nextDay in gaps) {
      if (0 in gaps[nextDay].gaps && gaps[nextDay].gaps[0].is_open === true) {
        let closingGap = 0

        for (const key in gaps[nextDay].gaps) {
          if (gaps[nextDay].gaps[key].is_open === false) {
            break
          }
          closingGap = gaps[nextDay].gaps[key].gap
        }

        if (gapsKey in gaps) {
          gaps[gapsKey].openingHours = timeFromGap(gaps[gapsKey].openingGap, true, false) + ' – ' + timeFromGap(closingGap, false, true, true)
        }
      }
    }
  }

  Vue.set(state, 'gaps', gaps)

  // Calculate earliest and latest gap from current and next day
  let earliest = 100
  let latest = 0

  if (state.arrivalTable.previous.date in state.gaps) {
    if (state.gaps[state.arrivalTable.previous.date].earliestGap > minimalGap && state.gaps[state.arrivalTable.previous.date].earliestGap < earliest) {
      earliest = state.gaps[state.arrivalTable.previous.date].earliestGap
    }
    if (state.gaps[state.arrivalTable.previous.date].latestGap > latest) {
      latest = state.gaps[state.arrivalTable.previous.date].latestGap
    }
  }

  if (state.arrivalTable.active.date in state.gaps) {
    if (state.gaps[state.arrivalTable.active.date].earliestGap < earliest && state.gaps[state.arrivalTable.active.date].earliestGap > minimalGap) {
      earliest = state.gaps[state.arrivalTable.active.date].earliestGap
    }
    if (state.gaps[state.arrivalTable.active.date].latestGap > latest) {
      latest = state.gaps[state.arrivalTable.active.date].latestGap
    }
  }

  if (state.arrivalTable.next.date in state.gaps) {
    if (state.gaps[state.arrivalTable.next.date].earliestGap < earliest && state.gaps[state.arrivalTable.next.date].earliestGap > minimalGap) {
      earliest = state.gaps[state.arrivalTable.next.date].earliestGap
    }
    if (state.gaps[state.arrivalTable.next.date].latestGap > latest) {
      latest = state.gaps[state.arrivalTable.next.date].latestGap
    }
  }

  if (latest > earliest) {
    state.arrivalTable.earliestGap = earliest
    state.arrivalTable.latestGap = latest
  }
}

/**
 * Update tours
 * @param {typeof import('./state.js').default} state
 * @param {Object[]} tours
 * @param {string} tours.von_zeit Time in xx:xx:xx format
 * @param {number} tours.fuehrung_frei Available spots
 */
export function updateTours (state, tours) {
  state.tours = tours

  const NEAR_OFFSET = 1

  const toursSpotsPerGap = {}

  for (const tour of tours) {
    const [hours, minutes] = tour.von_zeit.split(':')

    const decimalTime = parseInt(hours) + (minutes >= 30 ? 0.5 : 0)

    const nextHours = Math.floor(decimalTime - NEAR_OFFSET).toString().padStart(2, '0')
    const nextMinutes = ((decimalTime - NEAR_OFFSET) % 1 * 60).toString().padStart(2, '0')

    const nextGap = `${nextHours}:${nextMinutes}`

    if (nextGap in toursSpotsPerGap) {
      toursSpotsPerGap[nextGap] += tour.fuehrung_frei
    } else {
      toursSpotsPerGap[nextGap] = tour.fuehrung_frei
    }
  }

  state.toursSpotsPerGap = toursSpotsPerGap
}

/**
 * Update tours configuration
 * @param {typeof import('./state.js').default} state
 * @param {Object} config
 */
export function updateToursConfig (state, config) {
  state.toursConfig = config
}

/**
 * Set selected tours time
 * @param {typeof import('./state.js').default} state
 * @param {string} time Selected time
 */
export function setToursSelectedTime (state, time) {
  state.toursSelectedTime = time
}

export function setTourFilter (state, filter) {
  state.tourFilter = filter
}

export function updateEvents (state, payload) {
  const events = state.events
  events[payload.date] = payload.response.data[payload.date]

  const currentGapDate = new Date(state.arrivalTable.active.date)
  const minDate = dateHelper.dayBefore(currentGapDate, 3)
  const maxDate = dateHelper.dayAfter(currentGapDate, 3)

  for (const eventsKey in events) {
    const eventDate = new Date(eventsKey)

    if (eventDate < minDate || eventDate > maxDate) {
      delete events[eventsKey]
    }
  }

  Vue.set(state, 'events', events)
}

export function updateEventTeaserData (state, payload) {
  const eventTeaser = state.eventTeaser
  const now = new Date()

  for (const i in payload) {
    if (!(i in payload)) {
      continue
    }

    const teaserData = payload[i]
    if ('number' in teaserData) {
      eventTeaser[teaserData.number] = { lastRequest: now.getTime(), data: teaserData }
    }
  }

  Vue.set(state, 'eventTeaser', eventTeaser)
}

export function setEventTimes (state, payload) {
  const from = payload.from.split(':')
  from.pop()
  const until = payload.until.split(':')
  until.pop()
  state.selectedEventTimes = { from: from.join(':'), until: until.join(':') }
}

// Ticket

export function updateCart (state, payload) {
  if (!payload.dontResetSelection && payload.tickets.total_articles === 0) {
    state.ticketSelection.tickets = []
  }

  const articleTypes = []
  const selectionTypes = []

  for (const articleId in payload.tickets.articles) {
    for (let i = 0; i < payload.tickets.articles[articleId].count; i++) {
      articleTypes.push(parseInt(articleId))
      selectionTypes.push(parseInt(articleId))
    }
  }

  for (const eventId in payload.events.articles) {
    for (let i = 0; i < payload.events.articles[eventId].count; i++) {
      articleTypes.push(parseInt(eventId))
    }
  }

  state.cartArticleTypes = articleTypes

  if (selectionTypes.length) {
    state.ticketSelection.tickets = [...selectionTypes]
  }

  if (!payload.dontResetSelection) {
    state.selectedTicketsAmount = payload.tickets.total_articles
  }

  Vue.set(state, 'cart', payload)

  // EET
  tracking.eetTrackUpdateCart(state)

  state.cartLoading = false
}

export function setCartTimer (state, time) {
  state.cart.valid_until = time
}

export function setOrderReference (state, payload) {
  state.orderReference = payload
}

export function ticketSpecialEventRemove (state) {
  state.tickets.specialEvent = {
    active: false,
    date: '',
    name: '',
    time: '',
    types: [],
    total: 0
  }
}

export function setArticles (state, payload) {
  const articles = {}

  for (const aId in payload) {
    const article = payload[aId]
    articles[article.id] = article
  }

  state.articles = articles
}

export function updateReservationAmount (state, amount) {
  state.selectedReservationsAmount = parseInt(amount)
}

export function updateTicketNumber (state, ticketNumber) {
  state.selectedTicketsAmount = ticketNumber

  const ticketsSelected = state.ticketSelection.tickets
  const ticketsSelectedLength = state.ticketSelection.tickets.length

  if (ticketNumber < ticketsSelectedLength) {
    state.ticketSelection.tickets = ticketsSelected.splice(0, ticketNumber)
  } else if (ticketNumber > ticketsSelectedLength) {
    for (let i = 0; i < ticketNumber - ticketsSelectedLength; i++) {
      const newSelection = state.ticketSelection.tickets
      newSelection.push(1)
      state.ticketSelection.tickets = newSelection
    }
  }
}

export function ticketTypeSelected (state, ticketTypeUpdate) {
  Vue.set(state.ticketSelection.tickets, ticketTypeUpdate[0], ticketTypeUpdate[1])
}

export function updateVouchers (state, vouchers) {
  state.vouchers = vouchers
}

export function updateVoucherSelection (state, payload) {
  const tempSelection = state.voucherSelection
  tempSelection[payload.id] = payload.value
  Vue.set(state, 'voucherSelection', tempSelection)
}

export function setGiftcards (state, payload) {
  Vue.set(state, 'giftCards', payload)
}

export function setSelectedGiftcard (state, payload) {
  Vue.set(state, 'selectedGiftCard', payload)
}

export function setSelectedGiftcardTickets (state, payload) {
  Vue.set(state, 'selectedGiftCardTickets', payload)
}

export function setSelectedGiftcardTemplate (state, payload) {
  Vue.set(state, 'selectedGiftCardTemplate', payload)
}

// Yullbe

export function yullbeClassSelected (state, cls) {
  Vue.set(state, 'yullbeSelectedClass', cls)
}
